import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supportedLocales, translate, type Locale, type TranslationFunction } from './translations';

const STORAGE_KEY = 'availora.locale';

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslationFunction;
}

const I18nContext = createContext<I18nValue | null>(null);

function isSupportedLocale(value: string | null): value is Locale {
  return supportedLocales.includes(value as Locale);
}

/** Verwendet eine gespeicherte Auswahl, danach die Browsersprache und sonst Deutsch (Schweiz). */
function detectLocale(): Locale {
  const storedLocale = localStorage.getItem(STORAGE_KEY);
  if (isSupportedLocale(storedLocale)) return storedLocale;

  const browserLocales = navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  for (const locale of browserLocales) {
    if (locale.toLowerCase().startsWith('de')) return 'de-CH';
    if (locale.toLowerCase().startsWith('en')) return 'en';
  }
  return 'de-CH';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);
  const t = useCallback<TranslationFunction>((key, values) => translate(locale, key, values), [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nValue>(() => ({
    locale,
    t,
    setLocale: (nextLocale) => {
      localStorage.setItem(STORAGE_KEY, nextLocale);
      setLocaleState(nextLocale);
    }
  }), [locale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n muss innerhalb des I18nProvider verwendet werden.');
  return value;
}
